import { Controller } from "@hotwired/stimulus"
import { loadFramepay } from "@rebilly/framepay"

// Stimulus controller for FramePay card tokenization.
//
// Replaces raw card input fields with secure FramePay
// iFrames. On form submit, intercepts the submission,
// generates a payment token via FramePay, sets the token
// and a UUID payment_method_id in hidden fields, then
// submits the form to Rails.
//
// The controller auto-registers a submit listener on
// the nearest parent <form>, so it works whether the
// controller element IS the form or is nested inside one.
//
// Credentials are read from meta tags injected by the
// layout when ELEGY_PAYMENT_METHODS_ENABLED=true.
export default class extends Controller {
  static targets = [
    "cardNumber",
    "cardExpiration",
    "cardCvv",
    "tokenId",
    "paymentMethodId",
    "error"
  ]

  async connect() {
    this.parentForm = this.element.closest("form")
    if (this.parentForm) {
      this.boundSubmit = this.submit.bind(this)
      this.parentForm.addEventListener(
        "submit", this.boundSubmit
      )
    }

    const publishableKey = this.readMeta(
      "rebilly-publishable-key"
    )
    const organizationId = this.readMeta(
      "rebilly-organization-id"
    )
    const websiteId = this.readMeta("rebilly-website-id")

    console.log("FramePay: connect()", {
      publishableKey: publishableKey ? "present" : "MISSING",
      organizationId: organizationId || "MISSING",
      websiteId: websiteId || "MISSING",
      parentForm: this.parentForm ? "found" : "MISSING",
      metaTags: document.querySelectorAll(
        'meta[name^="rebilly"]'
      ).length + " rebilly meta tags in DOM"
    })

    if (!publishableKey) {
      console.warn(
        "FramePay: missing rebilly-publishable-key meta tag.",
        "Check that the layout includes the meta tags.",
        "This page uses layout:",
        document.querySelector('meta[name="layout"]')
          ?.content || "(unknown)"
      )
      return
    }

    try {
      this.framepay = await loadFramepay()

      this.framepay.initialize({
        publishableKey: publishableKey,
        organizationId: organizationId,
        websiteId: websiteId,
        transactionData: {
          amount: 0,
          currency: "USD"
        }
      })

      this.framepay.on("error", (error) => {
        console.error("FramePay error:", error)
        this.showError(error.message || "Payment error")
      })

      this.framepay.on("ready", () => {
        console.log("FramePay: ready, mounting card fields")
        this.mountCardFields()
      })
    } catch (error) {
      console.error("Failed to load FramePay:", error)
      this.showError("Failed to load payment form")
    }
  }

  disconnect() {
    if (this.parentForm && this.boundSubmit) {
      this.parentForm.removeEventListener(
        "submit", this.boundSubmit
      )
    }
    if (this.framepay) {
      try {
        this.framepay.destroy()
      } catch (e) {
        // Ignore cleanup errors
      }
    }
  }

  mountCardFields() {
    if (this.hasCardNumberTarget) {
      this.framepay.card.mount(
        this.cardNumberTarget, "cardNumber"
      )
    }
    if (this.hasCardExpirationTarget) {
      this.framepay.card.mount(
        this.cardExpirationTarget, "cardExpiration"
      )
    }
    if (this.hasCardCvvTarget) {
      this.framepay.card.mount(
        this.cardCvvTarget, "cardCvv"
      )
    }
  }

  async submit(event) {
    event.preventDefault()
    this.clearError()

    const submitBtn = this.parentForm
      ? this.parentForm.querySelector(
          "[type='submit'], button[type='submit']"
        )
      : null

    if (submitBtn) {
      submitBtn.disabled = true
      this.originalButtonText = submitBtn.value
        || submitBtn.textContent
      if (submitBtn.value) {
        submitBtn.value = "Processing..."
      } else {
        submitBtn.textContent = "Processing..."
      }
    }

    try {
      const form = this.parentForm || this.element
      const result = await this.framepay.createToken(form)

      if (result.error) {
        this.showError(result.error.message)
        this.resetSubmitButton(submitBtn)
        return
      }

      this.tokenIdTarget.value = result.id
      this.paymentMethodIdTarget.value = this.generateUUID()

      // Submit the form to Rails. Direct .submit() call
      // bypasses the event listener so we don't re-enter.
      form.submit()
    } catch (error) {
      console.error("FramePay tokenization failed:", error)
      this.showError(
        error.message || "Failed to process payment method"
      )
      this.resetSubmitButton(submitBtn)
    }
  }

  // -- Private helpers --

  readMeta(name) {
    const tag = document.querySelector(
      `meta[name="${name}"]`
    )
    return tag ? tag.content : null
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
    }
  }

  resetSubmitButton(btn) {
    if (btn) {
      btn.disabled = false
      if (btn.value) {
        btn.value = this.originalButtonText || "Submit"
      } else {
        btn.textContent = this.originalButtonText
          || "Submit"
      }
    }
  }

  generateUUID() {
    if (crypto && crypto.randomUUID) {
      return crypto.randomUUID()
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0
        const v = c === "x" ? r : (r & 0x3) | 0x8
        return v.toString(16)
      }
    )
  }
}
